"use client";

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Edit, Trash2, Plus, Calendar, Video, Filter, Users } from "lucide-react";
import AddMeeting from "./popups/AddMeeting";
import ModifierMeeting from "./popups/ModifierMeeting";
import DeleteMeeting from "./popups/DeleteMeeting";

const rawBackendUrl = process.env.REACT_APP_BACKEND_URL || "";
const backend_url = rawBackendUrl.startsWith("http://") || rawBackendUrl.startsWith("https://") ? rawBackendUrl : `https://${rawBackendUrl}`;
const API_URL = `${backend_url}/api`;

const formatDateTime = (dateTimeStr) => {
  const date = new Date(dateTimeStr);
  return date.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

const isMeetingActive = (dateTimeStr, duree) => {
  const now = new Date();
  const start = new Date(dateTimeStr);
  const end = new Date(start.getTime() + duree * 60000);
  return now >= start && now <= end;
};

const isMeetingPassed = (dateTimeStr, duree) => {
  const now = new Date();
  const end = new Date(new Date(dateTimeStr).getTime() + duree * 60000);
  return now > end;
};

const getToken = () => localStorage.getItem("access_token");

const EventCard = ({ event, onEdit, onDelete, onOpenDetails, onJoin }) => {
  const active = isMeetingActive(event.date_heure, event.duree);
  const passed = isMeetingPassed(event.date_heure, event.duree);
  const [hover, setHover] = useState(false);

  const getStatusStyle = () => {
    if (active) return { borderLeft: "4px solid #22c55e", background: "#f0fdf4" };
    if (passed) return { borderLeft: "4px solid #9ca3af", background: "#f9fafb", opacity: 0.8 };
    return { borderLeft: "4px solid #5e17eb" };
  };

  const getStatusText = () => {
    if (active) return "En cours";
    if (passed) return "Terminée";
    return "Planifiée";
  };

  const getStatusColor = () => {
    if (active) return "#22c55e";
    if (passed) return "#9ca3af";
    return "#5e17eb";
  };

  return (
    <div
      onClick={onOpenDetails}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        ...styles.card,
        ...getStatusStyle(),
        transform: hover ? "translateY(-6px) scale(1.03)" : "translateY(0)",
        boxShadow: hover
          ? "0 12px 30px rgba(94,23,235,0.25)"
          : "0 4px 14px rgba(0,0,0,0.06)",
      }}
    >
      <div style={styles.cardHeader}>
        <Calendar size={40} color="#5e17eb" />
      </div>

      <div style={styles.cardBody}>
        <div style={styles.cardHeaderRow}>
          <h3 style={styles.cardTitle}>{event.titre}</h3>
          <span style={{
            ...styles.statusBadge,
            backgroundColor: getStatusColor() + '20',
            color: getStatusColor()
          }}>
            {getStatusText()}
          </span>
        </div>

        <p style={styles.cardOrganizer}>
          Organisé par: <strong>{event.organisateur_nom || event.organisateur}</strong>
        </p>

        <p style={styles.cardDate}>
          {formatDateTime(event.date_heure)} ({event.duree} min)
        </p>
        <p style={styles.cardObjective}>{event.objectif}</p>
        <p style={styles.cardRoom}>📍 {event.salle}</p>

        <div style={styles.participants}>
          <Users size={14} color="#666" />
          <span>{event.participants_details?.length || 0} participants</span>
        </div>

        <button
          onClick={(e) => {
            e.stopPropagation();
            if (active) onJoin();
          }}
          disabled={!active}
          style={{
            ...styles.joinButton,
            backgroundColor: active ? "#16a34a" : "#e5e7eb",
            color: active ? "white" : "#9ca3af",
            cursor: active ? "pointer" : "not-allowed",
          }}
        >
          <Video size={18} />
          {active ? "Participer" : passed ? "Terminée" : "À venir"}
        </button>

        <div style={styles.cardActions}>
          {!passed && (
            <button onClick={(e) => { e.stopPropagation(); onEdit(); }} style={styles.editBtn}>
              <Edit size={18} />
            </button>
          )}
          
          <button onClick={(e) => { e.stopPropagation(); onDelete(); }} style={styles.deleteBtn}>
            <Trash2 size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

const ListeDesReunion = () => {
  const navigate = useNavigate();
  const [eventsData, setEventsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [openAddEvent, setOpenAddEvent] = useState(false);
  const [openModEvent, setOpenModEvent] = useState(false);
  const [openDelEvent, setOpenDelEvent] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const [filter, setFilter] = useState("all");

  const fetchReunions = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const token = getToken();
      if (!token) {
        setError("Vous devez être connecté");
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/reunions/`, {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      if (!response.ok) {
        throw new Error("Erreur lors du chargement des réunions");
      }

      const data = await response.json();

      let meetingsData = [];
      if (data.results) meetingsData = data.results;
      else if (Array.isArray(data)) meetingsData = data;
      else if (data.data) meetingsData = data.data;

      // Éviter les doublons
      const uniqueMeetings = [];
      const seenIds = new Set();
      for (const meeting of meetingsData) {
        if (!seenIds.has(meeting.id)) {
          seenIds.add(meeting.id);
          uniqueMeetings.push(meeting);
        }
      }

      setEventsData(uniqueMeetings);
    } catch (err) {
      console.error("Erreur fetch:", err);
      setError(err.message || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReunions();
  }, [fetchReunions]);

  const getFilteredEvents = () => {
    const now = new Date();

    return eventsData
      .filter(event => {
        const eventDate = new Date(event.date_heure);
        const eventEnd = new Date(eventDate.getTime() + (event.duree || 0) * 60000);

        switch (filter) {
          case "upcoming":
            return eventDate > now;
          case "passed":
            return eventEnd < now;
          default:
            return true;
        }
      })
      .sort((a, b) => {
        const aActive = isMeetingActive(a.date_heure, a.duree);
        const bActive = isMeetingActive(b.date_heure, b.duree);
        const aPassed = isMeetingPassed(a.date_heure, a.duree);
        const bPassed = isMeetingPassed(b.date_heure, b.duree);

        if (aActive && !bActive) return -1;
        if (!aActive && bActive) return 1;
        if (!aPassed && bPassed) return -1;
        if (aPassed && !bPassed) return 1;
        return new Date(a.date_heure) - new Date(b.date_heure);
      });
  };

  const handleAddEvent = async () => {
    await fetchReunions();
    setOpenAddEvent(false);
  };

  const handleEditEvent = async () => {
    await fetchReunions();
    setOpenModEvent(false);
    setSelectedEvent(null);
  };

  const handleDeleteEvent = async (id) => {
  if (!id) {
    console.error("ID de réunion invalide");
    return false;
  }

  const token = getToken();
  if (!token) {
    console.error("Vous devez être connecté");
    return false;
  }

  try {
    const response = await fetch(`${API_URL}/reunions/${id}/`, {
      method: "DELETE",
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json"
      }
    });

    if (response.status === 204 || response.ok) {
      await fetchReunions();
      setOpenDelEvent(false);
      setSelectedEvent(null);
      // REMOVED: alert("✅ Réunion supprimée avec succès!");
      return true;
    } else {
      let errorMessage = "Erreur lors de la suppression";
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorData.detail || errorMessage;
      } catch (e) {
        // Ignorer
      }
      throw new Error(errorMessage);
    }
  } catch (err) {
    console.error("Erreur delete:", err);
    // REMOVED: alert("❌ " + err.message);
    // You can still show error in the popup if needed
    return false;
  }
};
  const filteredEvents = getFilteredEvents();

  const totalEvents = eventsData.length;
  const activeEvents = eventsData.filter(e => isMeetingActive(e.date_heure, e.duree)).length;
  const upcomingEvents = eventsData.filter(e => new Date(e.date_heure) > new Date() && !isMeetingActive(e.date_heure, e.duree)).length;
  const passedEvents = eventsData.filter(e => {
    const end = new Date(new Date(e.date_heure).getTime() + (e.duree || 0) * 60000);
    return end < new Date();
  }).length;

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}></div>
          <p>Chargement des réunions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.page}>
        <div style={styles.errorContainer}>
          <p style={{ color: "#ef4444" }}>❌ {error}</p>
          <button onClick={fetchReunions} style={styles.retryButton}>
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <h1 style={styles.title}>LISTE DES RÉUNIONS POLYMEET</h1>
      <p style={styles.subtitle}>
        Gérez toutes les réunions de la plateforme
      </p>

      <div style={styles.statsBar}>
        <div style={styles.statItem}>
          <span style={styles.statValue}>{totalEvents}</span>
          <span style={styles.statLabel}>Total</span>
        </div>
        <div style={styles.statItem}>
          <span style={{ ...styles.statValue}}>{activeEvents}</span>
          <span style={styles.statLabel}>En cours</span>
        </div>
        <div style={styles.statItem}>
          <span style={{ ...styles.statValue}}>{upcomingEvents}</span>
          <span style={styles.statLabel}>À venir</span>
        </div>
        <div style={styles.statItem}>
          <span style={{ ...styles.statValue}}>{passedEvents}</span>
          <span style={styles.statLabel}>Passées</span>
        </div>
      </div>

      <div style={styles.topBar}>
        <div style={styles.filterGroup}>
          <Filter size={18} color="#666" />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={styles.filterSelect}
          >
            <option value="all">Toutes les réunions</option>
            <option value="upcoming">À venir</option>
            <option value="passed">Passées</option>
          </select>
        </div>

        <button style={styles.addBtn} onClick={() => setOpenAddEvent(true)}>
          <Plus size={18} /> Ajouter réunion
        </button>
      </div>

      {filteredEvents.length === 0 ? (
        <div style={styles.emptyState}>
          <Calendar size={48} color="#9ca3af" />
          <p>Aucune réunion à afficher</p>
          <button onClick={() => setOpenAddEvent(true)} style={styles.emptyAddBtn}>
            <Plus size={16} /> Créer une réunion
          </button>
        </div>
      ) : (
        <div style={styles.grid}>
          {filteredEvents.map((event) => (
            <EventCard
              key={event.id}
              event={event}
              onOpenDetails={() => navigate(`/reunions/${event.id}`)}
              onJoin={() => navigate(`/reunions/${event.id}/live`)}
              onEdit={() => {
                setSelectedEvent(event);
                setOpenModEvent(true);
              }}
              onDelete={() => {
                setSelectedEvent(event);
                setOpenDelEvent(true);
              }}
            />
          ))}
        </div>
      )}

      {openAddEvent && <AddMeeting closeAddEvent={() => setOpenAddEvent(false)} onEventAdded={handleAddEvent} />}
      {openModEvent && selectedEvent && <ModifierMeeting selectedEvent={selectedEvent} closeModEvent={() => setOpenModEvent(false)} onEventUpdated={handleEditEvent} />}
      {openDelEvent && selectedEvent && <DeleteMeeting selectedEvent={selectedEvent} closeDeleteEvent={() => setOpenDelEvent(false)} onEventDeleted={handleDeleteEvent} />}
    </div>
  );
};

export default ListeDesReunion;

const styles = {
  page: {
    padding: "22px",
    background: "#f7f6ff",
    minHeight: "100vh",
    borderRadius: "16px"
  },
  title: {
    fontSize: "26px",
    color: "#1f1f2e",
    marginBottom: "4px",
  },
  subtitle: {
    color: "#666",
    marginBottom: "18px",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "400px",
    gap: "16px"
  },
  spinner: {
    width: "40px",
    height: "40px",
    border: "3px solid #f3f3f3",
    borderTop: "3px solid #5e17eb",
    borderRadius: "50%",
    animation: "spin 1s linear infinite"
  },
  errorContainer: {
    textAlign: "center",
    padding: "40px",
    background: "white",
    borderRadius: "12px"
  },
  retryButton: {
    marginTop: "12px",
    padding: "8px 16px",
    background: "#5e17eb",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer"
  },
  statsBar: {
    display: "flex",
    gap: "24px",
    background: "white",
    padding: "16px 24px",
    borderRadius: "12px",
    marginBottom: "18px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)"
  },
  statItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center"
  },
  statValue: {
    fontSize: "24px",
    fontWeight: "bold",
    color: "#1f1f2e"
  },
  statLabel: {
    fontSize: "12px",
    color: "#666",
    marginTop: "4px"
  },
  topBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "18px",
    flexWrap: "wrap",
    gap: "12px"
  },
  filterGroup: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    background: "white",
    padding: "4px 12px",
    borderRadius: "8px",
    border: "1px solid #e5e7eb"
  },
  filterSelect: {
    padding: "8px",
    border: "none",
    background: "transparent",
    outline: "none",
    cursor: "pointer",
    fontSize: "14px",
    minWidth: "140px"
  },
  addBtn: {
    backgroundColor: "#5e17eb",
    color: "white",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "8px 16px",
    borderRadius: "8px",
    cursor: "pointer",
    border: "none",
    fontSize: "14px",
    fontWeight: "500"
  },
  emptyState: {
    textAlign: "center",
    padding: "60px",
    background: "white",
    borderRadius: "12px",
    color: "#6b7280"
  },
  emptyAddBtn: {
    marginTop: "16px",
    padding: "8px 16px",
    background: "#5e17eb",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px"
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
    gap: "16px"
  },
  card: {
    background: "white",
    borderRadius: "14px",
    cursor: "pointer",
    transition: "all 0.3s ease",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  cardHeader: {
    height: "120px",
    background: "#f3f0ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  cardBody: {
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    flex: 1
  },
  cardHeaderRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "8px",
    flexWrap: "wrap",
    gap: "8px"
  },
  cardTitle: {
    fontWeight: "600",
    fontSize: "16px",
    color: "#1f1f2e",
    margin: 0
  },
  statusBadge: {
    fontSize: "11px",
    padding: "4px 10px",
    borderRadius: "20px",
    fontWeight: "500"
  },
  cardOrganizer: {
    fontSize: "12px",
    color: "#5e17eb",
    marginBottom: "6px",
  },
  cardDate: {
    fontSize: "13px",
    color: "#888",
    marginBottom: "8px"
  },
  cardObjective: {
    fontSize: "13px",
    color: "#4b5563",
    marginBottom: "8px",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden"
  },
  cardRoom: {
    fontSize: "12px",
    color: "#6b7280",
    marginBottom: "8px"
  },
  participants: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "12px",
    color: "#666",
    marginBottom: "12px"
  },
  joinButton: {
    marginTop: "8px",
    padding: "8px 0",
    borderRadius: "8px",
    fontWeight: "500",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "6px",
    border: "none",
    width: "100%"
  },
  cardActions: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: "12px",
    gap: "8px"
  },
  editBtn: {
    color: "#f59e0b",
    padding: "6px",
    borderRadius: "6px",
    border: "none",
    background: "transparent",
    cursor: "pointer"
  },
  deleteBtn: {
    color: "#ef4444",
    padding: "6px",
    borderRadius: "6px",
    border: "none",
    background: "transparent",
    cursor: "pointer"
  },
};

const styleSheet = document.createElement("style");
styleSheet.innerText = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);